import { TrackGadget } from './track-gadget';
import { makeObservable, observable, reaction } from 'mobx';
import { EmptyIntervals, Intervals } from '@tikka/intervals/intervals';
import { NavigationState } from '../../chaat-player';
import { Context2D } from './canvas';

export class NavigationTrackGadget extends TrackGadget {
  @observable notifyNavigationSelect = 0;
  // TODO
  lastNavigationSelect: any = -1;
  lastTimeSelect = 0;

  handleIntervals: Intervals = null;
  doesRollover = true;
  showHandles = true;
  extendNavigationLines = 0;
  extendNavigationLinesOnRollover = false;
  handleFillStyle = '#000000';
  handleFillStyleOverride: (idx: number) => string = null;
  rolloverHandleFillStyle = '#FFEA95';
  navigationIndicatorFillStyle: string = null;
  navigationLinesStyle: string = null;
  navigationTimePoints: string = null;

  navigatorKey: string = null;
  navigation: NavigationState = null;
  // TODO rollover
  rolloverState: any = null;

  constructor() {
    super();
    this.handleIntervals = EmptyIntervals;
    makeObservable(this);
  }

  setHandleIntervals(intervals: Intervals) {
    if (this.handleIntervals === intervals) {
      return;
    }
    this.handleIntervals = intervals;
    this.requestFullRedraw();
  }

  getIntervalIndexForTime(time: number) {
    // TODO no need for method?
    return this.handleIntervals.containing(time);
  }

  setNavigation(navigation: NavigationState) {
    if (this.navigation === navigation) {
      return;
    }
    this.navigation = navigation;
    this.disposers.push(
      reaction(
        () => this.navigation.navigationPoint,
        () => this.handleChangeNavigationPoint()
      )
    );
  }

  handleChangeNavigationPoint() {
    if (
      this.navigation.navigationPoint &&
      this.navigation.navigationPoint.navigatorKey === this.navigatorKey
    ) {
      this.requestForegroundRedraw();
    }
  }

  getHandleTimeInterval(index: number) {
    return this.handleIntervals.intervalAt(index);
  }

  drawHandle(ctx: Context2D, index: number) {
    this.drawSimpleHandle(ctx, index);
  }

  drawSimpleHandle(ctx: Context2D, index: number) {
    const interval = this.getHandleTimeInterval(index);
    this.drawTimerangeRect(ctx, interval, this.height);
  }

  drawHandleOutline(ctx: Context2D, index: number) {
    const interval = this.getHandleTimeInterval(index);
    this.drawTimerangeRectOutline(ctx, interval, this.height);
  }

  drawTextHandle(ctx: Context2D, index: number, text: string) {
    const interval = this.getHandleTimeInterval(index);
    this.drawTextInTimeInterval(ctx, interval, text);
  }

  drawHandleEdges(ctx: Context2D, index: number, pixelHeight: number) {
    const interval = this.getHandleTimeInterval(index);
    // TODO use pixelHeight?
    this.drawTimerangeEdges(ctx, interval, this.height);
  }

  drawHandles(ctx: Context2D) {
    const range = this.handleIntervals.rangeIntersecting(
      this.canvasStartTime,
      this.canvasStartTime + this.canvasTimeExtent
    );
    if (range) {
      const rangeStart = range.begin;
      const rangeEnd = range.end;

      let currentFillStyle = this.handleFillStyle;
      for (let idx = rangeStart; idx <= rangeEnd; idx++) {
        if (this.handleFillStyleOverride) {
          const fillStyle = this.handleFillStyleOverride(idx);
          if (fillStyle && fillStyle !== currentFillStyle) {
            currentFillStyle = fillStyle;
            ctx.fillStyle = fillStyle;
          } else if (!fillStyle && currentFillStyle !== this.handleFillStyle) {
            currentFillStyle = this.handleFillStyle;
            ctx.fillStyle = this.handleFillStyle;
          }
        }
        this.drawHandle(ctx, idx);
      }
    }
  }

  drawNavigationLine(ctx: Context2D, index: number) {
    const x = this.timeToCanvasPixelX(this.getNavigationTime(index));
    const len = this.extendNavigationLines;
    if (len > 0) {
      ctx.moveTo(x, this.originY);
      ctx.lineTo(x, this.originY + len);
    } else {
      ctx.moveTo(x, this.bottomY);
      ctx.lineTo(x, this.bottomY - len);
    }
    ctx.stroke();
  }

  drawBackgroundLayer(ctx: Context2D) {
    if (this.showHandles) {
      ctx.save();
      ctx.fillStyle = this.handleFillStyle;
      this.drawHandles(ctx);
      ctx.restore();
    }
  }

  get showNavigation() {
    return this.navigatorKey;
  }

  drawForegroundLayer(ctx: Context2D) {
    if (this.showNavigation) {
      this.drawNavigationIndicator(ctx);
    }
    this.drawRolloverHighlight(ctx);
  }

  getVisibleNavigationIndex() {
    const navigationPoint = this.navigation.navigationPoint;
    if (
      !navigationPoint ||
      navigationPoint.navigatorKey !== this.navigatorKey
    ) {
      return -1;
    }
    const navigationInterval = this.handleIntervals.intervalAt(
      navigationPoint.index
    );
    if (
      !this.timeIsOnCanvas(navigationInterval.end) &&
      !this.timeIsOnCanvas(navigationInterval.begin)
    ) {
      if (navigationInterval.end < this.canvasStartTime) {
        return -1;
      }
      if (navigationInterval.begin > this.canvasEndTime) {
        return -1;
      }
    }
    return navigationPoint.index;
  }

  drawNavigationIndicator(ctx: Context2D) {
    const index = this.getVisibleNavigationIndex();
    if (index < 0) return;
    ctx.save();
    //ctx.fillStyle = this.navigationIndicatorFillStyle;
    //this.drawHandle(ctx, index);
    ctx.lineWidth = 3;
    this.drawHandleOutline(ctx, index);
    ctx.restore();
  }

  // TODO typing
  onNavigationSelect(f: (...a: any[]) => void) {
    this.disposers.push(
      reaction(
        () => this.notifyNavigationSelect,
        () => f(this, this.navigatorKey, this.lastNavigationSelect)
      )
    );
  }

  handleMouseClickAtTime(
    x: number,
    y: number,
    time: number,
    event: MouseEvent
  ) {
    const index = this.getIntervalIndexForTime(time);
    if (index < 0) {
      return;
    }
    const timeSelect = this.handleIntervals.intervalAt(index).begin;
    this.lastNavigationSelect = { index, event, time: timeSelect };
    this.notifyNavigationSelect++;
    return true; // TODO figure out why this not returning anything before
  }

  drawRolloverHighlight(ctx: Context2D) {
    if (!this.rolloverState) {
      return;
    }
    ctx.save();
    ctx.fillStyle = this.rolloverHandleFillStyle;
    this.drawHandle(ctx, this.rolloverState.index);
    ctx.lineWidth = 3;
    this.drawHandleOutline(ctx, this.rolloverState.index);
    ctx.restore();
  }

  // TODO rollover
  checkRollover(x: number, y: number, time: number, state: any) {
    if (state) {
      const interval = this.getHandleTimeInterval(state.index);
      if (interval.begin <= time && interval.end >= time) {
        return state;
      } else {
        // TODO maybe redundant with management code in Widget layer
        this.rolloverState = null;
        this.requestForegroundRedraw();
      }
    }
    const search = this.getIntervalIndexForTime(time);
    if (search >= 0) {
      return this.makeRolloverStateForIndex(search);
    }
    return null;
  }

  makeRolloverStateForIndex(index: number) {
    return { gadget: this, index, interval: this.getHandleTimeInterval(index) };
  }

  // TODO rollover
  setRolloverState(state: any) {
    if (state !== this.rolloverState) {
      this.requestForegroundRedraw();
    }
    this.rolloverState = state;
  }

  deactivateRollover() {
    if (this.rolloverState) {
      this.requestForegroundRedraw();
    }
    this.rolloverState = null;
  }

  drawActiveNavigationHandle() {}

  getNavigationTimes() {}

  getNavigationTime(index: number) {
    // TODO
    return 0;
  }
}
